/**
 * StatusBadge (LF-6146)
 *
 * The design system's semantic-state token. Where `Badge` carries structural
 * chips (counts, labels, tags) with no semantic colour, a `StatusBadge` says
 * whether a thing is good / waiting / broken — via a `tone`, never a
 * hand-pasted `className` colour.
 *
 * **Always bordered (VALE-1725).** Every pill in the app renders with a
 * visible border now — the owner's explicit call, on top of the earlier
 * `bordered`-vs-`subtle` emphasis split that let two structurally identical
 * status pills render with and without a border depending on which call site
 * wrote them, with no reliable rule distinguishing the two in practice. Six
 * tones, each on ONE canonical ramp: `border-<role>-subtle-border +
 * bg-<role>-subtle + the role's fg`; `neutral` keeps two fills
 * (`surface-subtle` / `muted`) because that ramp genuinely has two rungs
 * (VALE-524).
 *
 * The border was `border-<role>/20` until VALE-1758 — the SOLID role colour at
 * 20% composited over the fill, which made the edge the one part of the chip
 * whose hue was a function of two other tokens rather than its own. It drifted
 * independently of every other rung as a result. `-subtle-border` is a real
 * rung on the tone's ladder, and `__tests__/design-token-ramp-contract.test.ts`
 * holds it to the same lightness across all five tones and the same hue as the
 * rest of its own tone.
 *
 * `emphasis`/`StatusBadgeEmphasis` are kept as accepted-but-ignored for
 * source compatibility with existing call sites (dozens pass
 * `emphasis="subtle"`) rather than forcing a mechanical edit across every one
 * of them for a prop that no longer changes anything — every StatusBadge
 * renders bordered regardless of what it receives. Do not read this as
 * subtle still existing: it does not, and a new call site should not pass
 * the prop at all.
 *
 * This is the consolidation target the retired `StatusPill` evolved into — its
 * call sites, the ~90 ad-hoc custom-class Badges, and the three hand-rolled
 * status ladders all migrated onto it across LF-6149…LF-6156, and `StatusPill`
 * itself was deleted in LF-6157. This is now the only inline status token.
 */
import { type LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

export type StatusBadgeTone =
  | 'neutral'
  | 'info'
  | 'success'
  | 'caution'
  | 'critical'
  | 'ai';

/**
 * @deprecated no longer changes anything — every StatusBadge renders bordered
 * (VALE-1725). Kept only so existing call sites passing `emphasis="subtle"`
 * still type-check; do not pass this on a new call site.
 */
export type StatusBadgeEmphasis = 'bordered' | 'subtle';

/**
 * Tone → resolved classes, per emphasis. One canonical ramp per tone — no
 * `/60` alphas, no emerald-vs-green / rose-vs-red splits, no ad-hoc palette
 * paste (the drift the LF-6096 audit catalogued). A call site picks a tone +
 * emphasis and never writes a colour class.
 *
 * ── VALE-524: THESE ARE SEMANTIC TOKENS NOW, AND THE AA CONSTRAINT MOVED ────
 *
 * This map held raw light palette until VALE-524, and it was invisible to
 * `vale/no-raw-palette-colour` because the property keys (`neutral`,
 * `bordered`) are not class-shaped — so `components/ui` measured a clean zero
 * at `error` while every status chip in the app kept its light fill and could
 * not follow the theme. DARK-5b, DARK-5c and DARK-6a each found it from a
 * different surface; all six of DARK-6a's dark judge FAILs measured to this
 * map.
 *
 * The `text-800` rungs above were hand-picked because the RAW ramp missed AA:
 * green-700 on green-50 is 4.28:1 and amber-700 on amber-50 is 3.85:1. That
 * reasoning does not carry over, because the token pair is not the raw pair —
 * `--<role>-subtle-foreground` on `--<role>-subtle` measures **4.72–6.64:1 in
 * light and 8.57–10.27:1 in dark**, and is enumerated in
 * `__tests__/design-token-contrast.test.ts` at the 4.5:1 `text` tier. So the
 * constraint is now GATED rather than commented, which is strictly stronger.
 * Do not "restore" a hand-picked step here to chase it.
 *
 * ── WHY BOTH EMPHASES SHARE ONE FILL ───────────────────────────────────────
 *
 * The raw map gave `bordered` the -50 fill and `subtle` the -100 — a stronger
 * fill on the emphasis whose name and doc comment both say it should be the
 * QUIETER one. The vocabulary has one chromatic tint rung by DARK-2 decision,
 * so both now take `-subtle` and **the border carries the distinction, which is
 * exactly what `bordered` vs `subtle` names.** That aligns the component with
 * its own documented intent (subtle = for already-tinted grounds) instead of
 * preserving an accident of picking two Tailwind rungs.
 *
 * The alternative was measured and rejected: carrying the -100 rung as
 * `bg-<role>/<alpha>` puts `success` at **4.21–4.33:1 in light — under AA** at
 * the alpha that actually matches green-100, on the very tone whose comment
 * warns green is a low-contrast hue; and no single alpha is honest across the
 * hues (at `/10` it is Δ1 for `ai` against Δ35 for `warning`). Preserving it
 * properly would mean a second tint rung AND its own foreground rung — 10 new
 * tokens for DARK-8 to tune, to keep a difference the names do not describe.
 *
 * FOR DARK-8: `subtle` chips get a lighter fill in light (Δ11–21/255, the same
 * step-100 → `-subtle` substitution already shipped four times elsewhere) and
 * now read as borderless tints on 60 call sites. If that is too faint on a
 * tinted panel, the lever is the second tint rung above — a palette decision.
 *
 * `neutral` keeps its two rungs exactly, because the NEUTRAL ramp genuinely has
 * two (`--surface-subtle` and `--muted`, Δ2 and Δ4 from slate-50/slate-100).
 *
 * **VALE-1725 collapsed the `bordered`/`subtle` split.** Every tone now
 * resolves to one (bordered) string — the `subtle` fills above are retired,
 * not merely hidden, so do not resurrect a borderless rung here.
 */
const TONE: Record<StatusBadgeTone, string> = {
  neutral: 'border border-border bg-surface-subtle text-foreground-secondary',
  info: 'border border-info-subtle-border bg-info-subtle text-info-subtle-foreground',
  success:
    'border border-success-subtle-border bg-success-subtle text-success-subtle-foreground',
  caution:
    'border border-warning-subtle-border bg-warning-subtle text-warning-subtle-foreground',
  critical:
    'border border-error-subtle-border bg-error-subtle text-error-subtle-foreground',
  ai: 'border border-ai-subtle-border bg-ai-subtle text-ai-subtle-foreground'
};

/**
 * `quiet` — the tone survives, the fill does not (VALE-1758).
 *
 * A neutral chip carrying its tone as a leading dot, for the status that is
 * the EXPECTED state of most rows in a list. Eleven consecutive `Unsettled`
 * liabilities render eleven identical coloured chips, and at that density
 * colour stops being signal and becomes background texture — so the one row
 * that genuinely needs attention has nothing left to mark it with.
 *
 * It is a variant, not a component, and it is not a second vocabulary: the
 * call site passes the same `tone`, the dot is painted from that tone's own
 * `-subtle-foreground`, and switching a surface between the two forms is a
 * one-prop change. Reach for it when the tone is the answer for nearly every
 * row; leave it off when the tone genuinely distinguishes rows from each
 * other.
 */
const QUIET_TONE: Record<StatusBadgeTone, string> = {
  neutral: 'bg-foreground-secondary',
  info: 'bg-info-subtle-foreground',
  success: 'bg-success-subtle-foreground',
  caution: 'bg-warning-subtle-foreground',
  critical: 'bg-error-subtle-foreground',
  ai: 'bg-ai-subtle-foreground'
};

/** The quiet chip's own shell — neutral in every tone, so the dot is the tone. */
const QUIET_SHELL =
  'border border-border bg-surface-subtle text-foreground-secondary';

/** The one shared shape — medium weight, one radius, one size, icon-optional. */
export const STATUS_BADGE_BASE =
  'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs leading-none font-medium';

/**
 * The same shape with a leading icon (VALE-2130). The text-only padding put the
 * icon 10px from the edge but only 4px from its label, so the icon read as
 * drifting away from the word it belongs to. With an icon the leading edge
 * tightens to 8px and the icon-to-label gap opens to 6px. Written out in full
 * rather than merged over the base, so no class-ordering rule decides which
 * padding wins.
 */
export const STATUS_BADGE_BASE_WITH_ICON =
  'inline-flex items-center gap-1.5 rounded-full pl-2 pr-2.5 py-0.5 text-xs leading-none font-medium';

/**
 * Open to the rest of the span attributes (and a `ref`) on purpose — same as
 * `StatusPill`: a caller wanting a tooltip, `title`, or an aria/data hook needs
 * no wrapping `<span>`. `className` is for layout only (a `shrink-0`, a margin);
 * colour comes solely from `tone` + `emphasis`.
 */
export interface StatusBadgeProps extends React.ComponentPropsWithRef<'span'> {
  /** semantic tone — default 'neutral' */
  tone?: StatusBadgeTone;
  /**
   * @deprecated ignored — every StatusBadge renders bordered now (VALE-1725).
   * Accepted only so existing call sites still type-check; omit on new ones.
   */
  emphasis?: StatusBadgeEmphasis;
  /** optional leading Lucide icon; text-only is the default */
  icon?: LucideIcon;
  /**
   * Render the tone as a leading dot on a neutral chip instead of as a fill
   * (VALE-1758). For a status that is the expected state of most rows in a
   * list — see `QUIET_TONE` for when to reach for it.
   */
  quiet?: boolean;
  children: React.ReactNode;
}

export function StatusBadge({
  tone = 'neutral',
  emphasis: _emphasis,
  icon: Icon,
  quiet = false,
  children,
  className,
  ...props
}: StatusBadgeProps) {
  return (
    <span
      className={cn(
        Icon ? STATUS_BADGE_BASE_WITH_ICON : STATUS_BADGE_BASE,
        quiet ? QUIET_SHELL : TONE[tone],
        className
      )}
      {...props}
    >
      {quiet ? (
        <span
          className={cn('size-1.5 shrink-0 rounded-full', QUIET_TONE[tone])}
          aria-hidden
        />
      ) : null}
      {Icon ? <Icon className="h-3 w-3 shrink-0" aria-hidden /> : null}
      {children}
    </span>
  );
}

StatusBadge.displayName = 'StatusBadge';
